import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import MMAllegroSetupBanner from './MMAllegroSetupBanner';
import ApiloNotesButton from './ApiloNotesButton';
import ApiloNotesPanel from './ApiloNotesPanel';
import PaymentRefunds from './PaymentRefunds';

const API = '/api';

interface ReturnItem {
  offerId: string;
  name: string;
  quantity: number;
  price: { amount: string; currency: string; };
  reason: { type: string; userComment: string; };
  url: string;
  image?: string | null;
  sku?: string | null;
}

interface CustomerReturn {
  id: string;
  referenceNumber: string;
  createdAt: string;
  orderId: string;
  buyer: { login: string; email: string; };
  items: ReturnItem[];
  parcels: any[];
  status: string;
  refund: any;
  rejection: any;
  account_id: string;
  account_name: string;
  delivered_to_seller_at: string | null;
  accept_alert_dismissed_at: string | null;
  is_overdue: boolean;
  refunded_externally_at?: string | null;
  refunded_via_allegro_at?: string | null;
  refunded_via_allegro_amount?: number | string | null;
  isOrderOnly?: boolean;
}

const STATUS_LABELS: {[k:string]: {label:string, bg:string, color:string}} = {
  'CREATED':               { label: 'Nowy',               bg: '#fef2f2', color: '#dc2626' },
  'DELIVERED':             { label: 'Dostarczony',        bg: '#fef9c3', color: '#92400e' },
  'FINISHED':              { label: 'Zakończony',          bg: '#f0fdf4', color: '#15803d' },
  'COMMISSION_REFUNDED':   { label: 'Prowizja zwrócona',  bg: '#eff6ff', color: '#1d4ed8' },
  'COMMISSION_REFUND_CLAIMED': { label: 'Wniosek złożony', bg: '#f0fdf4', color: '#15803d' },
  'DISPATCHED':            { label: 'Nadany',              bg: '#eff6ff', color: '#1d4ed8' },
  'IN_TRANSIT':            { label: 'W transporcie',       bg: '#fef9c3', color: '#92400e' },
  'REJECTED':              { label: 'Odrzucony',           bg: '#fef2f2', color: '#dc2626' },
};

const REASON_LABELS: {[k:string]: string} = {
  'DONT_LIKE_IT':       'Nie odpowiada',
  'DAMAGED':            'Uszkodzony',
  'DIFFERENT':          'Inny niż w opisie',
  'INCOMPLETE':         'Niekompletny',
  'MISTAKE':            'Pomyłka',
  'OTHER_FLAW':         'Inna wada',
  'OVERDUE_DELIVERY':   'Spóźniona dostawa',
  'NONE':               'Brak powodu',
  'TOO_LARGE':          'Za duży',
  'TOO_SMALL':          'Za mały',
  'NOT_AS_EXPECTED':    'Nie spełnia oczekiwań',
  'ORDERED_FOR_COMPARISON': 'Zamówiony do porównania',
};

const REJECT_REASONS = [
  { code: 'NO_RETURN_RIGHT',      label: 'Towar nie podlega zwrotowi' },
  { code: 'BUSINESS_PURCHASE',    label: 'Zakup na firmę' },
  { code: 'ITEM_MISMATCH',        label: 'Zwrot niewłaściwego towaru' },
  { code: 'NEW_ITEM_SENT',        label: 'Wymiana towaru (wysłano nowy)' },
  { code: 'ITEM_FIXED',           label: 'Naprawa towaru' },
  { code: 'MISSING_PART_SENT',    label: 'Dosłanie brakujących elementów' },
  { code: 'REFUND_REJECTED',      label: 'Inny powód (podaj uzasadnienie)' },
];

export default function Returns({ onBack, hideHeader, currentUser = null, initialOverdueFilter = false, overdueNonce = 0, openReturnRef = '', openReturnNonce = 0 }: { onBack: () => void; hideHeader?: boolean; currentUser?: any; initialOverdueFilter?: boolean; overdueNonce?: number; openReturnRef?: string; openReturnNonce?: number }) {
  const [returns, setReturns] = useState<CustomerReturn[]>([]);
  const [selected, setSelected] = useState<CustomerReturn | null>(null);
  const [frame, setFrame] = useState<{ mode: 'messages' | 'issues' | 'apilo'; login: string } | null>(null);
  const [orderProducts, setOrderProducts] = useState<any[] | null>(null);
  const [apiloOrderNumber, setApiloOrderNumber] = useState<string | null>(null);
  const [convCounts, setConvCounts] = useState<{ messages: number; issues: number }>({ messages: 0, issues: 0 });
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<{id:string,name:string}[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [allegroBannerClosed, setAllegroBannerClosed] = useState(false);
  const [justReturnedFromAllegro] = useState(() => {
    try { return localStorage.getItem('mm_return_module') === 'returns'; } catch (e) { return false; }
  });
  useEffect(() => {
    if (justReturnedFromAllegro) { try { localStorage.removeItem('mm_return_module'); } catch (e) {} }
  }, [justReturnedFromAllegro]);
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterStatus, setFilterStatus] = useState(initialOverdueFilter ? '__overdue__' : 'all');
  const skipFirstOverdueEffect = useRef(true);
  useEffect(() => {
    if (skipFirstOverdueEffect.current) { skipFirstOverdueEffect.current = false; return; }
    setFilterStatus('__overdue__');
  }, [overdueNonce]);
  // Otwarcie konkretnego zwrotu po reference_number (z guzika "Zwroty" w Wiadomosciach/Zgloszeniach).
  // Pobiera zwrot dedykowanym zapytaniem (niezaleznie od aktualnych filtrow) i otwiera panel szczegolow.
  const skipFirstOpenRefEffect = useRef(true);
  useEffect(() => {
    // Pomijamy pierwszy przebieg tylko gdy nikt nie zazadal otwarcia (nonce===0).
    // Gdy komponent montuje sie z nonce>0 (klik "Zwroty" z Wiadomosci/Zgloszen) - otwieramy od razu.
    if (skipFirstOpenRefEffect.current) {
      skipFirstOpenRefEffect.current = false;
      if (openReturnNonce === 0) return;
    }
    const ref = (openReturnRef || '').trim();
    if (!ref) return;
    // Zamowienie BEZ zgloszenia zwrotu (z guzika Zwroty): ref "ORDER:<account_id>:<order_id>"
    if (ref.startsWith('ORDER:')) {
      const parts = ref.split(':');
      const accId = parts[1] || '';
      const ordId = parts.slice(2).join(':');
      if (!accId || !ordId) return;
      (async () => {
        try {
          const [detRes, accRes] = await Promise.all([
            axios.get(`${API}/allegro/orders/${ordId}/details?account_id=${accId}`),
            axios.get(`${API}/allegro/accounts`).catch(() => null),
          ]);
          const cf: any = detRes.data || {};
          const lis: any[] = cf.lineItems || [];
          const accList: any[] = (accRes && (accRes as any).data) || [];
          const accName = (accList.find((a: any) => a.id === accId) || {}).name || '';
          const pseudo: CustomerReturn = {
            id: 'order-' + ordId,
            referenceNumber: '',
            createdAt: (lis[0] && lis[0].boughtAt) || cf.updatedAt || '',
            orderId: ordId,
            buyer: { login: (cf.buyer && cf.buyer.login) || '', email: (cf.buyer && cf.buyer.email) || '' },
            items: lis.map((li: any) => ({
              offerId: (li.offer && li.offer.id) || '',
              name: (li.offer && li.offer.name) || '',
              quantity: li.quantity || 1,
              price: { amount: (li.price && li.price.amount) || '0', currency: (li.price && li.price.currency) || 'PLN' },
              reason: { type: '', userComment: '' },
              url: (li.offer && li.offer.id) ? `https://allegro.pl/oferta/${li.offer.id}` : '',
              image: null,
              sku: (li.offer && li.offer.external && li.offer.external.id) || null,
            })),
            parcels: [],
            status: 'ORDER',
            refund: null,
            rejection: null,
            account_id: accId,
            account_name: accName,
            delivered_to_seller_at: null,
            accept_alert_dismissed_at: null,
            is_overdue: false,
            isOrderOnly: true,
          };
          selectReturn(pseudo);
        } catch (e) { console.error(e); }
      })();
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/allegro/returns?limit=500&search=${encodeURIComponent(ref)}`);
        const list: CustomerReturn[] = res.data.returns || [];
        const found = list.find(x => x.referenceNumber === ref) || list[0];
        if (found) {
          setSearch('');
          setFilterStatus('all');
          setFilterAccount('all');
          setReturns(list);
          selectReturn(found);
        }
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReturnNonce]);
  const [search, setSearch] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [showRejectionReasonModal, setShowRejectionReasonModal] = useState(false);
  const [rejectionTemplates, setRejectionTemplates] = useState<any[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [rejectCode, setRejectCode] = useState('NO_RETURN_RIGHT');
  const [rejectDesc, setRejectDesc] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('REFUND');
  const [refundDelivery, setRefundDelivery] = useState(false);
  const [refundDeliveryAmount, setRefundDeliveryAmount] = useState('');
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [refundHistory, setRefundHistory] = useState<any[]>([]);
  // Wybor konkretnych pozycji do zwrotu: { [lineItemId]: kwota_string }.
  // Rozwiazuje blad, w ktorym kwota zwrotu byla rozmazywana proporcjonalnie na WSZYSTKIE
  // pozycje (takze juz zwrocone), co Allegro odrzucalo (dostepna 0,00).
  const [selectedLineItems, setSelectedLineItems] = useState<{[id: string]: string}>({});
  const [copyToast, setCopyToast] = useState('');
  const [orderData, setOrderData] = useState<any>(null);
  const [loadingOrderData, setLoadingOrderData] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const [activeTab, setActiveTab] = useState<'returns'|'refunds'>('returns');
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    localStorage.setItem('assistant_context', 'returns');
    return () => { localStorage.removeItem('assistant_context'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('assistant_context', activeTab === 'refunds' ? 'returns:refunds' : 'returns');
  }, [activeTab]);
  useEffect(() => {
    fetchReturns();
    axios.get(`${API}/allegro/accounts`).then(r => { setAccounts(r.data); setAccountsLoaded(true); }).catch(() => setAccountsLoaded(true));
    axios.get(`${API}/settings/reply-templates?category=rejection`).then(r => setRejectionTemplates(r.data.templates || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setFrame(null); }, [selected?.id]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/allegro/returns?limit=500${search.trim() ? '&search='+encodeURIComponent(search.trim()) : ''}${filterAccount !== 'all' ? '&account_id='+filterAccount : ''}${(filterStatus !== 'all' && filterStatus !== '__overdue__') ? '&status='+filterStatus : ''}`);
      setReturns(res.data.returns || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const selectReturn = async (r: CustomerReturn) => {
    setSelected(r);
    setActionMsg('');
    setPaymentInfo(null);
    setRefundHistory([]);
    setOrderData(null);
    setConvCounts({ messages: 0, issues: 0 });
    setLoadingOrderData(true);
    // Liczniki rozmow/zgloszen tego kupujacego - po company_id (wszystkie konta firmy), zgodnie z widokiem rozmow
    if (r.buyer?.login) {
      axios.get(`${API}/returns/conversation-counts?buyer_login=${encodeURIComponent(r.buyer.login)}`)
        .then((cr: any) => {
          setConvCounts({ messages: cr?.data?.messages || 0, issues: cr?.data?.issues || 0 });
        })
        .catch(() => {});
    }
    // Kontekst produktow zamowienia (pelna lista pozycji z Apilo + SKU + numer AM) dla sekcji Produkty.
    setOrderProducts(null);
    setApiloOrderNumber(null);
    if (!r.isOrderOnly) axios.get(`${API}/allegro/returns/${r.id}/order-products`).then((op: any) => {
      if (op?.data?.products) setOrderProducts(op.data.products);
      if (op?.data?.apilo_order_number) setApiloOrderNumber(op.data.apilo_order_number);
    }).catch(() => {});
    // Pobierz info o platnosci i zamowieniu rownoczesnie
    try {
      const [payRes, orderRes, refundsRes] = await Promise.allSettled([
        axios.get(`${API}/allegro/orders/${r.orderId}/details?account_id=${r.account_id}`),
        axios.get(`${API}/allegro/buyer-orders?buyer_login=${r.buyer?.login}&account_id=${r.account_id}`),
        axios.get(`${API}/allegro/orders/${r.orderId}/refunds?account_id=${r.account_id}`)
      ]);
      if (payRes.status === 'fulfilled') {
        const details = payRes.value.data;
        if (details?.payment?.id) {
          setPaymentInfo({
            payment: { id: details.payment.id },
            value: { amount: details.payment.paidAmount?.amount, currency: details.payment.paidAmount?.currency },
            deliveryCost: details.delivery?.cost,
            lineItems: details.lineItems || [],
          });
        }
      }
      if (refundsRes.status === 'fulfilled') {
        setRefundHistory(refundsRes.value.data?.refunds || []);
      }
      if (orderRes.status === 'fulfilled') {
        // Znajdz zamowienie po orderId
        const orders = orderRes.value.data.orders || [];
        const matchOrder = orders.find((o: any) => o.order_id === r.orderId);
        if (matchOrder) setOrderData(matchOrder);
        if (r.isOrderOnly) {
          if (matchOrder && matchOrder.apilo_id) setApiloOrderNumber(matchOrder.apilo_id);
          const imgMap: { [k: string]: string } = {};
          orders.filter((o: any) => o.order_id === r.orderId && o.image).forEach((o: any) => { imgMap[o.offer_id] = o.image; });
          if (Object.keys(imgMap).length > 0) setSelected(prev => (prev && prev.id === r.id) ? { ...prev, items: prev.items.map(it => ({ ...it, image: it.image || imgMap[it.offerId] || null })) } : prev);
        }
      }
    } catch(e) {}
    // Pobierz pelne dane zamowienia przez checkout-forms i SPLASZCZ dane kupujacego.
    // Nie nadpisujemy calego orderData surowym checkout-form (brak plaskich pol buyer_*),
    // tylko UZUPELNIAMY brakujace pola (wazne dla zwrotow z zanonimizowanym loginem Client:XXXX,
    // gdzie buyer-orders nie zwraca danych, ale checkout-form ma zagniezdzony buyer).
    try {
      const res = await axios.get(`${API}/allegro/orders/${r.orderId}/details?account_id=${r.account_id}`).catch(() => null);
      const cf = res?.data;
      if (cf) {
        const b = cf.buyer || {};
        const addr = (cf.delivery && cf.delivery.address) || {};
        const flat = {
          buyer_first_name: b.firstName,
          buyer_last_name: b.lastName,
          buyer_phone: b.phoneNumber,
          buyer_email: b.email,
          delivery_street: addr.street,
          delivery_city: addr.city,
          delivery_zip: addr.zipCode || addr.postCode,
        };
        setOrderData((prev: any) => {
          const base: any = prev || {};
          const merged: any = { ...base };
          for (const k of Object.keys(flat)) {
            if (!merged[k] && (flat as any)[k]) merged[k] = (flat as any)[k];
          }
          return merged;
        });
      }
    } catch(e) {}
    setLoadingOrderData(false);
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    return new Date(d).toLocaleString('pl-PL', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });
  };

  const totalValue = (r: CustomerReturn) => {
    const sum = r.items.reduce((acc, item) => acc + parseFloat(item.price.amount) * item.quantity, 0);
    return sum.toFixed(2);
  };
  // Ile jeszcze mozna zwrocic z danej pozycji (lineItem):
  // cena pozycji minus suma juz wykonanych zwrotow dopasowanych po lineItem id.
  const getLineItemBalance = (li: any) => {
    const price = parseFloat(li.price?.amount || '0') * (li.quantity || 1);
    let refunded = 0;
    for (const rf of refundHistory) {
      const liIds = (rf.lineItems || []).map((x: any) => x.id);
      if (liIds.includes(li.id)) {
        const val = parseFloat(rf.totalValue?.amount || '0');
        refunded += liIds.length === 1 ? val : val / liIds.length;
      }
    }
    const available = Math.max(0, price - refunded);
    return { price, refunded, available };
  };

  // Reset strony przy zmianie filtrow
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); fetchReturns(); }, [search, filterAccount, filterStatus]);

  // is_overdue liczone na serwerze (identycznie jak licznik w LiveAlertBar) - nie przeliczamy
  // tu lokalnie przez Date.now(), zeby liczby w pasku i na tej stronie nigdy sie nie rozjechaly
  // przez rozne "teraz" (zegar przegladarki vs serwera).
  const isReturnOverdue = (r: CustomerReturn) => !!r.is_overdue;
  const filtered = returns.filter(r => {
    if (filterAccount !== 'all' && r.account_id !== filterAccount) return false;
    if (filterStatus === '__overdue__') {
      if (!isReturnOverdue(r)) return false;
    } else if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (search.trim()) {
      const s = search.trim().replace(/\s+/g, " ").toLowerCase();
      const matchLogin = r.buyer?.login?.toLowerCase().includes(s);
      const matchRef = r.referenceNumber?.toLowerCase().includes(s);
      const matchItem = r.items?.some((i: ReturnItem) => i.name?.toLowerCase().includes(s));
      const matchWaybill = r.parcels?.some((p: any) =>
        p.waybill?.toLowerCase().includes(s) ||
        p.transportingWaybill?.toLowerCase().includes(s)
      );
      const matchPhone = r.parcels?.some((p: any) =>
        p.sender?.phoneNumber?.replace(/\s/g,'').includes(s.replace(/\s/g,''))
      );
      if (!matchLogin && !matchRef && !matchItem && !matchWaybill && !matchPhone) return false;
    }
    return true;
  });

  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const ReturnList = () => (
    <>
      {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Ładowanie...</div>}
      {paginated.map(r => {
        const st = STATUS_LABELS[r.status] || { label: r.status, bg: '#f3f4f6', color: '#6b7280' };
        const isOverdue = isReturnOverdue(r);
        return (
          <div key={r.id} onClick={() => selectReturn(r)}
            style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
              background: selected?.id === r.id ? '#eff6ff' : (isOverdue ? '#fef2f2' : '#fff'),
              borderLeft: selected?.id === r.id ? '3px solid #1d4ed8' : (isOverdue ? '3px solid #dc2626' : '3px solid transparent') }}>
            {isOverdue && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 6 }}>
                <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                  ⚠ Zaakceptuj zwrot — odebrano {Math.floor((Date.now() - new Date(r.delivered_to_seller_at!).getTime()) / (24*60*60*1000))} dni temu
                </span>
                <button onClick={async e => {
                    e.stopPropagation();
                    try {
                      await axios.post(`${API}/allegro/returns/${r.id}/dismiss-alert`);
                      setReturns(prev => prev.map(x => x.id === r.id ? { ...x, accept_alert_dismissed_at: new Date().toISOString() } : x));
                    } catch (err) { console.error(err); }
                  }}
                  style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
                  ✓ Oznacz jako zrobione
                </button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontWeight: 500, fontSize: 12 }}>
                <span title="Kliknij aby skopiować" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(r.buyer?.login); setCopyToast(r.buyer?.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>
                  {r.buyer?.login}
                </span>
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(r.createdAt)}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.referenceNumber} · {r.items?.map(i => i.name).join(', ')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: st.bg, color: st.color }}>
                {st.label}
              </span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>{totalValue(r)} {r.items?.[0]?.price?.currency || 'PLN'}</span>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>{r.account_name}</span>
              </div>
            </div>
          </div>
        );
      })}
      {filtered.length === 0 && !loading && (
        <div style={{ padding: 20, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>Brak zwrotów</div>
      )}
    </>
  );

  const printReturn = (r: CustomerReturn, od?: any) => {
    const st = STATUS_LABELS[r.status] || {label: r.status, bg: '#f3f4f6', color: '#6b7280'};
    const total = r.items.reduce((acc, i) => acc + parseFloat(i.price.amount) * i.quantity, 0).toFixed(2);
    const currency = r.items[0]?.price?.currency || 'PLN';
    const itemsRows = r.items.map(item =>
      '<tr>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px">' + (item.name || '—') + '</td>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:center">' + item.quantity + '</td>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:right">' + item.price.amount + ' ' + item.price.currency + '</td>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px">' + (REASON_LABELS[item.reason?.type] || item.reason?.type || '—') + '</td>' +
      '</tr>'
    ).join('');
    const parcelsRows = r.parcels.map((p: any) =>
      '<tr>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px">' + (p.carrierId || '—') + (p.transportingCarrierId && p.transportingCarrierId !== p.carrierId ? ' → ' + p.transportingCarrierId : '') + '</td>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;font-family:monospace">' + (p.waybill || p.transportingWaybill || '—') + '</td>' +
      '<td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px">' + (p.sender?.phoneNumber || '—') + '</td>' +
      '</tr>'
    ).join('');
    const refundSection = r.refund
      ? '<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px 18px;margin-bottom:16px">' +
        '<div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:6px">&#10003; Zwrot płatności zaksięgowany</div>' +
        '<div style="font-size:13px">Kwota: <strong>' + (r.refund.value?.amount || '') + ' ' + (r.refund.value?.currency || '') + '</strong></div>' +
        '<div style="font-size:12px;color:#6b7280;margin-top:4px">Status: ' + (r.refund.status || '') + '</div></div>'
      : '<div style="background:#fef9c3;border:1px solid #fde68a;border-radius:8px;padding:14px 18px;margin-bottom:16px">' +
        '<div style="font-size:13px;font-weight:600;color:#92400e">&#9203; Zwrot płatności nie został jeszcze zaksięgowany</div></div>';
    const parcelsSection = r.parcels?.length > 0
      ? '<div style="margin-bottom:16px"><div style="font-size:14px;font-weight:700;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #e5e7eb">Przesyłka</div>' +
        '<table style="width:100%;border-collapse:collapse"><tr style="background:#f8fafc">' +
        '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Przewoźnik</th>' +
        '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Nr listu</th>' +
        '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Tel. nadawcy</th></tr>' +
        parcelsRows + '</table></div>' : '';
    const html = '<!DOCTYPE html><html><head><title>Zwrot ' + r.referenceNumber + '</title>' +
      '<style>body{font-family:Arial,sans-serif;margin:0;padding:32px}@media print{body{padding:16px}}</style>' +
      '</head><body><div style="max-width:700px;margin:0 auto;border:2px solid #1d4ed8;border-radius:8px;padding:32px">' +
      '<div style="display:flex;justify-content:space-between;margin-bottom:24px">' +
      '<div><div style="font-size:22px;font-weight:700;color:#1d4ed8">Potwierdzenie zwrotu towaru</div>' +
      '<div style="font-size:13px;color:#6b7280;margin-top:4px">OPENkontakt &middot; openkontakt.com</div></div>' +
      '<div style="text-align:right"><div style="font-size:12px;color:#6b7280">Data wydruku:</div>' +
      '<div style="font-size:13px;font-weight:600">' + new Date().toLocaleString('pl-PL') + '</div></div></div>' +
      '<table style="width:100%;border-collapse:collapse;margin-bottom:20px">' +
      '<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;width:40%">Nr referencyjny:</td>' +
      '<td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:700">' + r.referenceNumber + '</td></tr>' +
      '<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">ID zamówienia:</td>' +
      '<td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-family:monospace">' + r.orderId + '</td></tr>' +
      '<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Data złożenia:</td>' +
      '<td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">' + new Date(r.createdAt).toLocaleString('pl-PL') + '</td></tr>' +
      '<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Status:</td>' +
      '<td style="padding:8px 12px;border:1px solid #e5e7eb;font-size:12px;font-weight:600;background:' + st.bg + ';color:' + st.color + '">' + st.label + '</td></tr>' +
      '<tr><td style="padding:8px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Konto Allegro:</td>' +
      '<td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">' + r.account_name + '</td></tr></table>' +
      '<div style="margin-bottom:16px"><div style="font-size:14px;font-weight:700;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #e5e7eb">Kupuj&#x105;cy</div>' +
      '<table style="width:100%;border-collapse:collapse">' +
      '<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;width:40%">Login Allegro:</td>' +
      '<td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">' + (r.buyer?.login || '—') + '</td></tr>' +
      (od?.buyer_first_name || od?.buyer_last_name ? '<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Imi&#x119; i nazwisko:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px;font-weight:600">' + ((od.buyer_first_name||'') + ' ' + (od.buyer_last_name||'')).trim() + '</td></tr>' : '') +
      '<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Email:</td>' +
      '<td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">' + (r.buyer?.email || '—') + '</td></tr>' +
      (od?.buyer_phone ? '<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Telefon:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">' + od.buyer_phone + '</td></tr>' : '') +
      (od?.delivery_street ? '<tr><td style="padding:6px 12px;background:#f8fafc;border:1px solid #e5e7eb;font-size:12px;color:#6b7280">Adres:</td><td style="padding:6px 12px;background:#fff;border:1px solid #e5e7eb;font-size:12px">' + od.delivery_street + ', ' + (od.delivery_zip||'') + ' ' + (od.delivery_city||'') + '</td></tr>' : '') +
      '</table></div>' +
      '<div style="margin-bottom:16px"><div style="font-size:14px;font-weight:700;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #e5e7eb">Produkty</div>' +
      '<table style="width:100%;border-collapse:collapse"><tr style="background:#f8fafc">' +
      '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Nazwa</th>' +
      '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:center">Ilo&#x15B;&#x107;</th>' +
      '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:right">Cena</th>' +
      '<th style="padding:6px 8px;border:1px solid #e5e7eb;font-size:12px;text-align:left">Pow&#xF3;d</th></tr>' +
      itemsRows + '</table></div>' +
      parcelsSection + refundSection +
      '<div style="background:#1d4ed8;color:#fff;padding:14px 18px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">' +
      '<div style="font-size:14px;font-weight:600">WARTO&#x15A;&#x106; ZWROTU</div>' +
      '<div style="font-size:22px;font-weight:700">' + total + ' ' + currency + '</div></div>' +
      '<div style="margin-top:20px;font-size:11px;color:#9ca3af;text-align:center;border-top:1px solid #e5e7eb;padding-top:12px">' +
      'Dokument wygenerowany przez OPENkontakt &middot; openkontakt.com</div></div>' +
      '<script>window.onload=function(){window.print();}<\/script></body></html>'; // eslint-disable-line no-useless-escape
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  };

  const ReturnDetail = () => {
    if (!selected) return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>Wybierz zwrot</div>
        </div>
      </div>
    );

    const st = selected.isOrderOnly ? { label: 'Zamówienie bez zgłoszenia zwrotu', bg: '#f3f4f6', color: '#374151' } : (STATUS_LABELS[selected.status] || { label: selected.status, bg: '#f3f4f6', color: '#6b7280' });

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
            {selected.isOrderOnly ? 'Zamówienie' : 'Zwrot ' + selected.referenceNumber} –{' '}
            <span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.buyer?.login); setCopyToast(selected.buyer?.login); setTimeout(() => setCopyToast(''), 1500); }} style={{ cursor: 'copy' }}>
              {selected.buyer?.login}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: st.bg, color: st.color }}>
              {st.label}
            </span>
            <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#f0fdf4', color: '#15803d' }}>
              {selected.account_name}
            </span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              Zamówienie: {selected.orderId?.substring(0,8)}...
            </span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              {formatDate(selected.createdAt)}
            </span>
          </div>
          {/* Akcje - logika:
               canAct = brak odrzucenia I status nie FINISHED I nie oznaczono jako zplacony poza Allegro
               Przyciski widoczne dla WSZYSTKICH statusow jesli canAct=true
               Zamiast przyciskow: badge "Odrzucony" (klikalny -> powod) lub "Oplacony" */}
          {(() => {
            const isRejected = !!selected.rejection;
            const isFinished = selected.status === 'FINISHED' || !!selected.refunded_externally_at || !!selected.refunded_via_allegro_at;
            const canAct = !isRejected && !isFinished;
            return (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {canAct && (
                  <button onClick={() => { setSelectedLineItems({}); setRefundAmount(''); setShowRefundModal(true); }}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                      border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer' }}>
                    💰 Zwróć pieniądze przez Allegro
                  </button>
                )}
                {canAct && !selected.isOrderOnly && (
                  <button onClick={async () => {
                    if (!window.confirm('Potwierdzasz że pieniądze zostały zwrócone klientowi poza systemem Allegro (np. przelewem bankowym)?')) return;
                    setActionLoading(true);
                    try {
                      await axios.post(`${API}/allegro/returns/${selected.id}/mark-external-refund`);
                      setActionMsg('✓ Oznaczono jako zwrócone poza Allegro');
                      fetchReturns();
                    } catch(e: any) { setActionMsg('Błąd: ' + (e.response?.data?.detail || 'nieznany')); }
                    setActionLoading(false);
                  }} disabled={actionLoading}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                      border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}>
                    ✓ Zwrócono poza Allegro
                  </button>
                )}
                {canAct && !selected.isOrderOnly && (
                  <button onClick={() => setShowRejectModal(true)}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                      border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                    ✗ Odrzuć zwrot
                  </button>
                )}
                {isRejected && (
                  <button onClick={() => setShowRejectionReasonModal(true)}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                      border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                    ✗ Odrzucony — kliknij aby zobaczyć powód
                  </button>
                )}
                {isFinished && (
                  <span style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                    border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d' }}>
                    ✓ {selected.refunded_via_allegro_at
                        ? ('Zwrócono przez Allegro' + (selected.refunded_via_allegro_amount ? ': ' + Number(selected.refunded_via_allegro_amount).toFixed(2) + ' zł' : ''))
                        : (selected.refunded_externally_at ? 'Opłacono poza Allegro' : 'Opłacono')}
                  </span>
                )}
              </div>
            );
          })()}
          <div style={{ marginTop: 6, display: selected.isOrderOnly ? 'none' : undefined }}>
            <button onClick={() => printReturn(selected, orderData)}
              style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8,
                border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', cursor: 'pointer' }}>
              🖨️ Drukuj potwierdzenie
            </button>
          </div>
          {actionMsg && (
            <div style={{ marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6,
              background: actionMsg.includes('Błąd') ? '#fef2f2' : '#f0fdf4',
              color: actionMsg.includes('Błąd') ? '#dc2626' : '#15803d' }}>
              {actionMsg}
            </div>
          )}
        </div>

        {/* Szczegoly */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', background: '#f9fafb' }}>
          {/* Dane kupujacego */}
          {loadingOrderData && (
            <div style={{ fontSize: 12, color: "#6b7280", padding: "8px 0" }}>⏳ Ładowanie danych kupującego...</div>
          )}
          {!loadingOrderData && orderData && (orderData.buyer_first_name || orderData.buyer_phone || orderData.delivery_street) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Dane kupującego</div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 12, border: '1px solid #e5e7eb' }}>
                {(orderData.buyer_first_name || orderData.buyer_last_name) && (
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: '#6b7280' }}>Imię i nazwisko: </span>
                    <span style={{ fontWeight: 600 }}>{orderData.buyer_first_name} {orderData.buyer_last_name}</span>
                  </div>
                )}
                {orderData.buyer_phone && (
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: '#6b7280' }}>Telefon: </span>
                    <span style={{ fontWeight: 500 }}>{orderData.buyer_phone}</span>
                  </div>
                )}
                {orderData.buyer_email && (
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: '#6b7280' }}>Email: </span>
                    <span style={{ fontWeight: 500 }}>{orderData.buyer_email}</span>
                  </div>
                )}
                {orderData.delivery_street && (
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: '#6b7280' }}>Adres: </span>
                    <span style={{ fontWeight: 500 }}>{orderData.delivery_street}, {orderData.delivery_zip} {orderData.delivery_city}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Produkty */}
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Produkty</div>
          {apiloOrderNumber && (
            <div style={{ fontSize: 12, color: '#111827', marginBottom: 8 }}>
              Zamówienie Apilo: <b style={{ fontFamily: 'monospace' }}>{apiloOrderNumber}</b>
            </div>
          )}
          {orderProducts && orderProducts.filter((p: any) => !p.reported).length > 0 && (
            <>
              {orderProducts.filter((p: any) => !p.reported).map((p: any, i: number) => (
                <div key={'nr' + i} style={{ background: '#f9fafb', opacity: 0.75, borderRadius: 8, padding: '10px 14px', marginBottom: 8,
                  border: '1px solid #e5e7eb', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {p.image
                    ? <img src={p.image} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                    : <div style={{ width: 54, height: 54, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📦</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#374151', flex: 1, marginRight: 8 }}>{p.name}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {p.quantity_ordered} × {p.price_amount} {p.price_currency || selected.items?.[0]?.price?.currency || 'PLN'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {p.sku && (
                        <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontFamily: 'monospace' }}>SKU: {p.sku}</span>
                      )}
                      <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 8, background: '#f3f4f6', color: '#6b7280', fontWeight: 600 }}>Nie zgłoszony do zwrotu</span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {selected.items.map((item, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 8,
              border: '1px solid #e5e7eb', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {item.image
                ? <img src={item.image} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                : <div style={{ width: 54, height: 54, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📦</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <a href={item.url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 13, fontWeight: 500, color: '#1d4ed8', textDecoration: 'none', flex: 1, marginRight: 8 }}>
                    {item.name}
                  </a>
                  <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {item.quantity} × {item.price.amount} {item.price.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {item.sku && (
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontFamily: 'monospace' }}>SKU: {item.sku}</span>
                  )}
                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 8, background: '#fef9c3', color: '#92400e' }}>
                    {selected.isOrderOnly ? 'Bez zgłoszenia zwrotu' : (REASON_LABELS[item.reason?.type] || item.reason?.type)}
                  </span>
                  {item.reason?.userComment && (
                    <span style={{ fontSize: 11, color: '#6b7280' }}>„{item.reason.userComment}"</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Podsumowanie wartości */}
          <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 12,
            border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{selected.isOrderOnly ? 'Wartość zamówienia (produkty):' : 'Łączna wartość zwrotu:'}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>{totalValue(selected)} {selected.items?.[0]?.price?.currency || 'PLN'}</span>
          </div>

          {/* Przesylka */}
          {selected.parcels?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Przesyłka</div>
              {selected.parcels.map((p, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 8, border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 13, marginBottom: 3 }}>
                    <span style={{ color: '#6b7280' }}>Przewoźnik: </span>{p.carrierId}
                    {p.transportingCarrierId && p.transportingCarrierId !== p.carrierId && ` → ${p.transportingCarrierId}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#374151' }}>
                    <span style={{ color: '#6b7280' }}>Nr listu: </span>
                    <span style={{ fontFamily: 'monospace' }}>{p.waybill || p.transportingWaybill || '—'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
                    <span style={{ color: '#6b7280' }}>Klient odesłał: </span>{formatDate(p.createdAt)}
                  </div>
                  {i === 0 && selected.delivered_to_seller_at && (
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
                      <span style={{ color: '#6b7280' }}>Sprzedawca otrzymał: </span>{formatDate(selected.delivered_to_seller_at)}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {/* Info o platnosci */}
          {paymentInfo && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>Płatność</div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 13 }}>
                  <span style={{ color: '#6b7280' }}>ID płatności: </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{paymentInfo.payment?.id}</span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  <span style={{ color: '#6b7280' }}>Kwota: </span>
                  <span style={{ fontWeight: 600 }}>{paymentInfo.value?.amount} {paymentInfo.value?.currency}</span>
                </div>
                {paymentInfo.deliveryCost && (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    <span style={{ color: '#6b7280' }}>Koszt dostawy: </span>
                    <span style={{ fontWeight: 600 }}>{paymentInfo.deliveryCost.amount} {paymentInfo.deliveryCost.currency}</span>
                  </div>
                )}
                {paymentInfo.deliveryCost && (
                  <div style={{ fontSize: 13, marginTop: 4, paddingTop: 4, borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ color: '#6b7280' }}>Łącznie (produkty + dostawa): </span>
                    <span style={{ fontWeight: 700 }}>{(parseFloat(totalValue(selected)) + parseFloat(paymentInfo.deliveryCost.amount)).toFixed(2)} {paymentInfo.deliveryCost.currency}</span>
                  </div>
                )}
                {refundHistory.filter((rf: any) => rf.status === 'SUCCESS' || rf.status === 'IN_PROGRESS').map((rf: any, i: number) => (
                  <div key={i} style={{ fontSize: 13, marginTop: 4, color: '#16a34a', fontWeight: 600 }}>
                    ✓ {rf.status === 'IN_PROGRESS' ? 'Zwrot w realizacji' : 'Zwrócono pieniądze'}: {formatDate(rf.createdAt)} — {rf.totalValue?.amount} {rf.totalValue?.currency}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Odrzucenie */}
          {selected.rejection && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 8, textTransform: 'uppercase' }}>Odrzucono</div>
              <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', border: '1px solid #fecaca' }}>
                <div style={{ fontSize: 13 }}>{selected.rejection.code}</div>
                {selected.rejection.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{selected.rejection.description}</div>}
              </div>
            </>
          )}

          {/* Zwrot platnosci */}
          {selected.refund && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 8, textTransform: 'uppercase' }}>Zwrot płatności</div>
              <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', border: '1px solid #bbf7d0' }}>
                {selected.refund.value?.amount && (
                  <div style={{ fontSize: 13 }}>Kwota: <strong>{selected.refund.value?.amount} {selected.refund.value?.currency}</strong></div>
                )}
                {selected.refund.status && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Status: {selected.refund.status}</div>
                )}
                {selected.refund.bankAccount && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 6 }}>💳 Dane do przelewu</div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <span style={{ color: '#6b7280', fontSize: 11 }}>Właściciel: </span>
                      <strong
                        title="Kliknij aby skopiować"
                        onClick={() => { navigator.clipboard.writeText(selected.refund.bankAccount.owner); setActionMsg('✓ Skopiowano właściciela konta'); setTimeout(() => setActionMsg(''), 1500); }}
                        style={{ cursor: 'copy', color: '#1d4ed8', textDecoration: 'underline dotted' }}>
                        {selected.refund.bankAccount.owner}
                      </strong>
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <span style={{ color: '#6b7280', fontSize: 11 }}>Nr konta: </span>
                      <span
                        title="Kliknij aby skopiować"
                        onClick={() => { navigator.clipboard.writeText(selected.refund.bankAccount.iban || selected.refund.bankAccount.accountNumber); setActionMsg('✓ Skopiowano numer konta'); setTimeout(() => setActionMsg(''), 1500); }}
                        style={{ fontFamily: 'monospace', fontSize: 12, cursor: 'copy', color: '#1d4ed8', textDecoration: 'underline dotted' }}>
                        {selected.refund.bankAccount.iban || selected.refund.bankAccount.accountNumber}
                      </span>
                    </div>
                    {selected.refund.bankAccount.swift && (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>SWIFT: {selected.refund.bankAccount.swift}</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {/* Dane do przelewu gdy brak refund ale jest COD - mozna dodac w przyszlosci */}

          {/* Rozmowy z kupujacym (jak w module Przesylki) */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginTop: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Rozmowy z kupującym</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                onClick={() => selected.buyer?.login && setFrame({ mode: 'messages', login: selected.buyer.login })}
                disabled={!selected.buyer?.login}
                style={{
                  padding: '10px 14px', borderRadius: 8, cursor: selected.buyer?.login ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 13,
                  background: frame?.mode === 'messages' ? '#1d4ed8' : '#eff6ff',
                  color: frame?.mode === 'messages' ? '#fff' : '#1d4ed8',
                  border: '1px solid #bfdbfe',
                }}>
                Wiadomości ({convCounts.messages})
              </button>
              <button
                onClick={() => selected.buyer?.login && setFrame({ mode: 'issues', login: selected.buyer.login })}
                disabled={!selected.buyer?.login}
                style={{
                  padding: '10px 14px', borderRadius: 8, cursor: selected.buyer?.login ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 13,
                  background: frame?.mode === 'issues' ? '#b45309' : '#fffbeb',
                  color: frame?.mode === 'issues' ? '#fff' : '#b45309',
                  border: '1px solid #fde68a',
                }}>
                Dyskusje/Reklamacje ({convCounts.issues})
              </button>
              {selected.orderId && (
                <button
                  onClick={() => setFrame(frame?.mode === 'apilo' ? null : { mode: 'apilo', login: selected.orderId })}
                  style={{
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    background: frame?.mode === 'apilo' ? '#6d28d9' : '#f5f3ff',
                    color: frame?.mode === 'apilo' ? '#fff' : '#6d28d9',
                    border: '1px solid #ddd6fe',
                  }}>
                  {'\ud83d\udcdd Apilo - CRM'}
                </button>
              )}
              {frame && (
                <button onClick={() => setFrame(null)}
                  style={{ padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                    background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#6b7280' }}>
                  Zwiń ✕
                </button>
              )}
              <a href={selected.buyer?.login ? `/?open=messages&login=${encodeURIComponent(selected.buyer.login)}` : '#'}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: '#6b7280', textDecoration: 'underline', marginLeft: 'auto' }}>
                otwórz w nowej karcie ↗
              </a>
            </div>
            {frame && frame.mode === 'apilo' && (
              <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                  {'\ud83d\udcdd Wiadomości z Apilo'}
                </div>
                <ApiloNotesPanel orderId={frame.login} />
              </div>
            )}
            {frame && frame.mode !== 'apilo' && (
              <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, color: '#6b7280' }}>
                  {frame.mode === 'messages' ? 'Wiadomości' : 'Dyskusje/Reklamacje'} — {frame.login}
                </div>
                <iframe
                  key={frame.mode + frame.login}
                  title="rozmowy"
                  src={`/?open=${frame.mode}&login=${encodeURIComponent(frame.login)}`}
                  style={{ width: '100%', height: 620, border: 'none', display: 'block' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
      {((accountsLoaded && accounts.length === 0) || justReturnedFromAllegro) && !allegroBannerClosed && (
        <MMAllegroSetupBanner
          message="Aby zarządzać zwrotami od kupujących, podłącz konto Allegro."
          returnModule="returns"
          showCongrats={justReturnedFromAllegro}
          onClose={() => setAllegroBannerClosed(true)}
        />
      )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}

      {/* MODAL ODRZUCENIA */}
      {showRejectionReasonModal && selected?.rejection && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowRejectionReasonModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '95vw' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: '#dc2626' }}>✗ Powód odrzucenia zwrotu</div>
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '12px 14px', border: '1px solid #fecaca', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{selected.rejection.code}</div>
              {selected.rejection.reason && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{selected.rejection.reason}</div>}
              {selected.rejection.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{selected.rejection.description}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRejectionReasonModal(false)}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: 'transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
      {showRejectModal && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '95vw' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>✗ Odrzuć zwrot</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Powód odrzucenia</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {REJECT_REASONS.map(r => (
                  <button key={r.code} onClick={() => setRejectCode(r.code)}
                    style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left',
                      border: '2px solid ' + (rejectCode === r.code ? '#dc2626' : '#d1d5db'),
                      background: rejectCode === r.code ? '#fef2f2' : 'transparent',
                      color: rejectCode === r.code ? '#dc2626' : '#374151',
                      fontWeight: rejectCode === r.code ? 600 : 400 }}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {rejectionTemplates.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Wzory odpowiedzi:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {rejectionTemplates.map((t: any) => (
                    <button key={t.id} onClick={() => setRejectDesc(t.text)}
                      style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20,
                        border: '1px solid #bfdbfe', background: '#eff6ff',
                        color: '#1d4ed8', cursor: 'pointer' }}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Uzasadnienie <span style={{ color: '#dc2626' }}>*</span> <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>(wymagane przez Allegro, max 700 znaków)</span></div>
              <textarea value={rejectDesc} onChange={e => setRejectDesc(e.target.value)}
                rows={3} placeholder="Opisz powód odrzucenia zwrotu..."
                style={{ width: '100%', padding: '8px', border: '1px solid ' + (rejectDesc.trim() ? '#d1d5db' : '#fca5a5'), borderRadius: 6,
                  fontSize: 13, fontFamily: 'system-ui', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
              {!rejectDesc.trim() && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>Uzasadnienie jest wymagane</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectDesc(''); }}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: 'transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Anuluj
              </button>
              <button onClick={async () => {
                if (!rejectDesc.trim()) { return; }
                setActionLoading(true);
                try {
                  const res = await axios.post(`${API}/allegro/returns/${selected.id}/reject`, {
                    account_id: selected.account_id, reason_code: rejectCode, reason_description: rejectDesc.trim()
                  });
                  if (res.data?.status_code >= 400) {
                    setActionMsg('Błąd: ' + JSON.stringify(res.data?.data));
                  } else {
                    setActionMsg('✓ Zwrot odrzucony');
                    setShowRejectModal(false);
                    fetchReturns();
                  }
                } catch(e: any) { setActionMsg('Błąd: ' + (e.response?.data?.detail || 'nieznany')); }
                setActionLoading(false);
              }} disabled={actionLoading}
                style={{ padding: '8px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                {actionLoading ? '⏳' : 'Odrzuć zwrot'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ZWROTU PLATNOSCI */}
      {showRefundModal && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '95vw' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>💰 Zwrot płatności</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              {selected.isOrderOnly ? 'Wartość zamówienia: ' : 'Wartość zwrotu: '}<strong>{totalValue(selected)} {selected.items?.[0]?.price?.currency || 'PLN'}</strong>
            </div>
            {!paymentInfo && (
              <div style={{ padding: '10px', background: '#fef9c3', borderRadius: 6, fontSize: 12, color: '#92400e', marginBottom: 12 }}>
                ⚠️ Nie znaleziono informacji o płatności. Podaj ID płatności ręcznie.
              </div>
            )}
            {paymentInfo?.lineItems && paymentInfo.lineItems.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Wybierz pozycje do zwrotu</div>
                {paymentInfo.lineItems.map((li: any) => {
                  const bal = getLineItemBalance(li);
                  const cur = li.price?.currency || 'PLN';
                  const disabled = bal.available <= 0.001;
                  const checked = selectedLineItems[li.id] !== undefined;
                  const name = (li.offer?.name || li.name || 'Pozycja');
                  return (
                    <label key={li.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', border: '1px solid ' + (checked ? '#93c5fd' : '#e5e7eb'), borderRadius: 8, marginBottom: 6, background: disabled ? '#f9fafb' : (checked ? '#eff6ff' : '#fff'), cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
                      <input type="checkbox" disabled={disabled} checked={checked}
                        onChange={e => {
                          setSelectedLineItems(prev => {
                            const next = { ...prev };
                            if (e.target.checked) { next[li.id] = bal.available.toFixed(2); }
                            else { delete next[li.id]; }
                            // Auto-wpisz sume zaznaczonych pozycji do pola kwoty (mozna nadpisac recznie)
                            const suma = Object.values(next).reduce((s: number, a: any) => s + parseFloat(a || '0'), 0);
                            const cnt = Object.keys(next).length;
                            setRefundAmount(prevAmt => {
                              if (cnt === 0) return '';
                              if (cnt === 1 && prevAmt && prevAmt.trim() !== '') return prevAmt;
                              return suma.toFixed(2);
                            });
                            return next;
                          });
                        }}
                        style={{ marginTop: 3, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          Cena: {bal.price.toFixed(2)} {cur}
                          {bal.refunded > 0.001 && <> · zwrócono: {bal.refunded.toFixed(2)} {cur}</>}
                          {' '}· <strong style={{ color: disabled ? '#dc2626' : '#059669' }}>dostępne: {bal.available.toFixed(2)} {cur}</strong>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Kwota zwrotu ({selected.items?.[0]?.price?.currency || 'PLN'})</div>
              <input value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
                placeholder={totalValue(selected)}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                  fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Zostaw puste aby zwrócić pełną kwotę ({totalValue(selected)} {selected.items?.[0]?.price?.currency || 'PLN'})</div>
            </div>
            {paymentInfo?.deliveryCost && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={refundDelivery} onChange={e => {
                    setRefundDelivery(e.target.checked);
                    if (e.target.checked && !refundDeliveryAmount) setRefundDeliveryAmount(paymentInfo.deliveryCost.amount);
                  }} />
                  Zwróć też koszt dostawy ({paymentInfo.deliveryCost.amount} {paymentInfo.deliveryCost.currency})
                </label>
                {refundDelivery && (
                  <input value={refundDeliveryAmount} onChange={e => setRefundDeliveryAmount(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6,
                      fontSize: 13, outline: 'none', boxSizing: 'border-box', marginTop: 8 }} />
                )}
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Powód zwrotu</div>
              <select value={refundReason} onChange={e => setRefundReason(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                <option value="REFUND">Zwrot towaru</option>
                <option value="COMPLAINT">Reklamacja</option>
                <option value="PRODUCT_NOT_RECEIVED">Produkt nie dotarł</option>
                <option value="PRODUCT_NOT_AS_DESCRIBED">Produkt niezgodny z opisem</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRefundModal(false); setRefundAmount(''); }}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', background: 'transparent', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Anuluj
              </button>
              <button onClick={async () => {
                if (!paymentInfo?.payment?.id) {
                  setActionMsg('Błąd: brak ID płatności');
                  setShowRefundModal(false);
                  return;
                }
                setActionLoading(true);
                const items = paymentInfo.lineItems || [];
                // Buduj line_items TYLKO z zaznaczonych pozycji (selectedLineItems),
                // z kwotami z salda. Nie rozmazujemy proporcjonalnie na wszystkie pozycje
                // (to powodowalo blad "dostepna 0,00" gdy ktoras pozycja byla juz zwrocona).
                let lineItemsToSend: any[] = [];
                const selIds = Object.keys(selectedLineItems);
                const manualAmt = (refundAmount || '').trim().replace(',', '.');
                if (selected.isOrderOnly && selIds.length === 0 && items.length > 1) { window.alert('Zaznacz pozycje zamówienia, za które chcesz zwrócić pieniądze.'); setActionLoading(false); return; }
                if (selIds.length <= 1 && items.length >= 1) {
                  const targetId = selIds.length === 1 ? selIds[0] : items[0].id;
                  const fallback = selIds.length === 1
                    ? String(selectedLineItems[targetId])
                    : (selected.isOrderOnly && items[0] ? getLineItemBalance(items[0]).available.toFixed(2) : totalValue(selected));
                  const amt = manualAmt || fallback.replace(',', '.');
                  lineItemsToSend = [{ id: targetId, amount: amt }];
                } else {
                  lineItemsToSend = Object.entries(selectedLineItems).map(([id, amt]) => ({
                    id, amount: String(amt).replace(',', '.')
                  }));
                }
                if (lineItemsToSend.length === 0) {
                  setActionMsg('Wybierz przynajmniej jedną pozycję do zwrotu');
                  setActionLoading(false);
                  return;
                }
                const selCurrency = selected.items?.[0]?.price?.currency || 'PLN';
                const _sumConfirm = lineItemsToSend.reduce((s: number, it: any) => s + parseFloat(it.amount || '0'), 0)
                  + (refundDelivery ? parseFloat((refundDeliveryAmount || '0').replace(',', '.')) : 0);
                if (!window.confirm('Potwierdz zwrot: ' + _sumConfirm.toFixed(2) + ' ' + selCurrency + ' za zamowienie. Kontynuowac?')) {
                  setActionLoading(false);
                  return;
                }
                try {
                  const res = await axios.post(`${API}/allegro/orders/refund`, {
                    account_id: selected.account_id,
                    payment_id: paymentInfo.payment.id,
                    order_id: selected.orderId,
                    line_items: lineItemsToSend,
                    delivery_amount: refundDelivery ? refundDeliveryAmount.replace(',', '.') : '',
                    currency: selCurrency,
                    reason: refundReason
                  });
                  if (res.data?.status_code >= 400) {
                    const errCode = res.data?.data?.errors?.[0]?.code;
                    if (errCode === 'COD_NOT_PAID') {
                      setActionMsg('⚠️ Nie można zwrócić pieniędzy przez Allegro — zamówienie za pobraniem nie zostało jeszcze opłacone (klient mógł nie odebrać paczki, albo kurier jeszcze nie rozliczył płatności z Allegro). To nie jest błąd aplikacji. Jeśli mimo to rozliczasz zwrot z klientem inną drogą, użyj przycisku "Zwrócono poza Allegro".');
                    } else if (errCode === 'PAYMENT_NOT_FOUND') {
                      setActionMsg('⚠️ Allegro nie znalazło tej płatności — spróbuj ponownie za chwilę lub sprawdź zamówienie bezpośrednio w Allegro.');
                    } else {
                      setActionMsg('Błąd: ' + JSON.stringify(res.data?.data));
                    }
                  } else {
                    const _sumSent = lineItemsToSend.reduce((s: number, it: any) => s + parseFloat(it.amount || '0'), 0).toFixed(2);
                    setActionMsg('✓ Zwrot płatności zlecony: ' + _sumSent + ' ' + selCurrency);
                    setShowRefundModal(false);
                    setRefundAmount('');
                    fetchReturns();
                  }
                } catch(e: any) { setActionMsg('Błąd: ' + (e.response?.data?.detail || 'nieznany')); }
                setActionLoading(false);
              }} disabled={actionLoading}
                style={{ padding: '8px 18px', background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                {actionLoading ? '⏳' : '💰 Zlec zwrot'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* ZAKŁADKA WYDRUKI */}
        {activeTab === 'refunds' && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <PaymentRefunds accounts={accounts} onBack={() => setActiveTab("returns")} />
          </div>
        )}

        {/* ZAKŁADKA ZWROTY */}
        {activeTab === 'returns' && <>
        {/* SIDEBAR */}
        {(!isMobile || !selected) && (
          <div style={{ width: isMobile ? '100%' : 340, borderRight: '1px solid #e5e7eb',
            display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', background: '#f9fafb' }}>
            {!hideHeader && (
              <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 15 }}>📦 Zwroty</span>
                <button onClick={onBack} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6,
                  border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>← Wróć</button>
              </div>
            )}
            {/* Zakładki */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', background: '#fff' }}>
              <button onClick={() => setActiveTab('returns')}
                style={{ flex: 1, padding: '9px 0', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 12, fontWeight: activeTab === 'returns' ? 700 : 400,
                  color: activeTab === 'returns' ? '#1d4ed8' : '#6b7280',
                  borderBottom: activeTab === 'returns' ? '2px solid #1d4ed8' : '2px solid transparent',
                  marginBottom: -2 }}>
                📦 Zwroty
              </button>
              <button onClick={() => setActiveTab('refunds')}
                style={{ flex: 1, padding: '9px 0', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 12, fontWeight: (activeTab as string) === 'refunds' ? 700 : 400,
                  color: (activeTab as string) === 'refunds' ? '#1d4ed8' : '#6b7280',
                  borderBottom: (activeTab as string) === 'refunds' ? '2px solid #1d4ed8' : '2px solid transparent',
                  marginBottom: -2 }}>
                💰 Wydruki
              </button>
            </div>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Login, nr ref., nr paczki, tel..."
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none' }} />
                <button onClick={fetchReturns}
                  style={{ padding: '6px 10px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>↻</button>
              </div>
              <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
                style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: '#fff', marginBottom: 6 }}>
                <option value="all">Wszystkie konta</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                <option value="all">Wszystkie statusy</option>
                <option value="__overdue__">⚠ Zwroty do rozpatrzenia</option>
                {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <ReturnList />
            </div>
            <div style={{ padding: '6px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}
                style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                  background: 'transparent', cursor: page === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
              <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>
                {page+1}/{Math.max(1, Math.ceil(filtered.length/PAGE_SIZE))} · {filtered.length} zwrotów
              </span>
              <button onClick={() => setPage(p => p+1)} disabled={(page+1)*PAGE_SIZE >= filtered.length}
                style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                  background: 'transparent', cursor: (page+1)*PAGE_SIZE >= filtered.length ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
            </div>
          </div>
        )}

        {/* DETAIL */}
        {(!isMobile || selected) && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {isMobile && selected && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                <button onClick={() => setSelected(null)}
                  style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
                  ← Wróć do listy
                </button>
              </div>
            )}
            {ReturnDetail()}
          </div>
        )}
        </>}
      </div>
    </div>
  );
}
